import {
  Badge,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular, Edit16Regular, PersonAdd16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { SectionTitle } from "@/components/ui-typography";
import { FuiButton, FuiFilterSelect, FuiSearchBox } from "@/components/fui-control";
import { useCatalogStore } from "@/store/catalog-store";
import type { UserResource } from "@/engine/types";
import { ResourceFormDialog } from "./ResourceDialogs";
import { ResourceProfilePanel } from "./ResourceProfilePanel";
import { EMPLOYMENT_LABEL } from "./resource-meta";

const useStyles = makeStyles({
  drawer: {
    width: "840px",
    maxWidth: "100vw",
    transitionProperty: "width",
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  drawerCollapsed: {
    width: "300px",
  },
  backdrop: {
    backdropFilter: "blur(2.5px)",
    WebkitBackdropFilter: "blur(2.5px)",
    backgroundColor: "rgba(0, 0, 0, 0.16)",
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  body: {
    padding: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 300px",
    height: "100%",
    overflow: "hidden",
  },
  bodyCollapsed: {
    gridTemplateColumns: "1fr",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  listSplit: {
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  listTools: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 9fr) minmax(0, 11fr)",
    gap: tokens.spacingHorizontalXS,
    width: "100%",
  },
  filterSelect: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: tokens.spacingVerticalXS,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `6px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    border: "1px solid transparent",
    textAlign: "left",
    backgroundColor: "transparent",
    width: "100%",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowOn: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow2,
  },
  rowText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  util: {
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  detail: {
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    overflowY: "auto",
    minHeight: 0,
  },
});

type Filter = "all" | "available" | "over" | "external";

/**
 * Browse the enterprise pool without leaving the current page: search + filter on the right,
 * full profile with cross-project load expanding to the left on selection. In a project,
 * "이 플랜에 배정" hands the selected person to the assignment dialog.
 */
export function ResourcePoolDrawer({
  open,
  onClose,
  assignedResourceIds,
  onAssign,
  masked,
  initialResourceId,
}: {
  open: boolean;
  onClose: () => void;
  assignedResourceIds?: string[];
  onAssign?: (resourceId: string) => void;
  masked?: boolean;
  initialResourceId?: string | null;
}) {
  const styles = useStyles();
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const projects = useCatalogStore((s) => s.projects);
  const getWorkload = useCatalogStore((s) => s.getResourceWorkload);
  const updateResource = useCatalogStore((s) => s.updateResource);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [teamFilter, setTeamFilter] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(initialResourceId ?? null);
  const [editing, setEditing] = useState<UserResource | null>(null);

  useEffect(() => {
    if (open) setSelectedId(initialResourceId ?? null);
  }, [open, initialResourceId]);

  const workloads = useMemo(() => {
    const m: Record<string, ReturnType<typeof getWorkload>> = {};
    for (const id of Object.keys(resources)) m[id] = getWorkload(id);
    return m;
  }, [resources, projects, getWorkload]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(resources)
      .filter((r) => {
        if (teamFilter && r.teamId !== teamFilter) return false;
        const wl = workloads[r.id];
        if (filter === "available" && (wl?.totalAllocationRatio ?? 0) >= 0.95) return false;
        if (filter === "over" && !wl?.isOverAllocated) return false;
        if (filter === "external" && r.employmentType === "permanent") return false;
        if (!q) return true;
        return [r.displayName, r.roleTitle, r.email, ...r.skills, EMPLOYMENT_LABEL[r.employmentType]].some((s) => s.toLowerCase().includes(q));
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName, "ko"));
  }, [resources, query, filter, teamFilter, workloads]);

  const selected = selectedId ? resources[selectedId] : undefined;
  const assignedSet = useMemo(() => new Set(assignedResourceIds ?? []), [assignedResourceIds]);
  const showDetail = Boolean(selected);

  const overloadedCount = useMemo(() => {
    return Object.values(resources).filter((r) => workloads[r.id]?.isOverAllocated).length;
  }, [resources, workloads]);

  const teamOptions = useMemo(
    () => [
      { value: "", label: "모든 팀" },
      ...Object.values(teams).map((t) => ({ value: t.id, label: t.name })),
    ],
    [teams]
  );

  const filterOptions = useMemo(
    () => [
      { value: "all", label: "전체" },
      { value: "available", label: "투입 가능" },
      { value: "over", label: overloadedCount > 0 ? `과부하 (${overloadedCount})` : "과부하" },
      { value: "external", label: "프리·외주" },
    ],
    [overloadedCount]
  );

  return (
    <>
    <OverlayDrawer
      open={open}
      position="end"
      modalType={editing ? "non-modal" : "modal"}
      className={mergeClasses(styles.drawer, !showDetail && styles.drawerCollapsed)}
      backdrop={{ className: styles.backdrop }}
      onOpenChange={(_, d) => !d.open && onClose()}
      aria-label="인력풀"
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headerRow}>
          <DrawerHeaderTitle>
            <SectionTitle>인력풀</SectionTitle>
            <Caption1 style={{ marginLeft: 8, color: tokens.colorNeutralForeground3 }}>{Object.keys(resources).length}명</Caption1>
          </DrawerHeaderTitle>
          <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={onClose} />
        </div>
      </DrawerHeader>
      <DrawerBody className={mergeClasses(styles.body, !showDetail && styles.bodyCollapsed)}>
        {selected && (
          <div className={styles.detail}>
            <ResourceProfilePanel
              resource={selected}
              team={selected.teamId ? teams[selected.teamId] : undefined}
              workload={workloads[selected.id] ?? getWorkload(selected.id)}
              masked={masked}
              actions={
                <>
                  <FuiButton appearance="subtle" density="dialog" icon={<Edit16Regular />} onClick={() => setEditing(selected)}>
                    편집
                  </FuiButton>
                  {onAssign && (
                    <FuiButton appearance="primary" density="dialog" icon={<PersonAdd16Regular />} disabled={assignedSet.has(selected.id)} onClick={() => onAssign(selected.id)}>
                      {assignedSet.has(selected.id) ? "배정됨" : "이 플랜에 배정"}
                    </FuiButton>
                  )}
                </>
              }
            />
          </div>
        )}
        <div className={mergeClasses(styles.list, showDetail && styles.listSplit)}>
          <div className={styles.listTools}>
            <FuiSearchBox density="dialog" value={query} onChange={(_, d) => setQuery(d.value)} placeholder="이름, 역할, 기술 검색" style={{ width: "100%" }} />
            <div className={styles.filterRow}>
              <FuiFilterSelect
                density="dialog"
                label="필터"
                value={filter}
                options={filterOptions}
                onValueChange={(v) => setFilter(v as Filter)}
                className={styles.filterSelect}
              />
              <FuiFilterSelect
                density="dialog"
                label="팀"
                value={teamFilter}
                options={teamOptions}
                onValueChange={setTeamFilter}
                className={styles.filterSelect}
              />
            </div>
          </div>
          <div className={styles.scroll}>
            {list.map((r) => {
              const wl = workloads[r.id];
              const pct = Math.round((wl?.totalAllocationRatio ?? 0) * 100);
              const on = r.id === selectedId;
              const already = assignedSet.has(r.id);
              return (
                <button key={r.id} type="button" className={mergeClasses(styles.row, on && styles.rowOn)} onClick={() => setSelectedId(r.id)}>
                  <PersonAvatar name={r.displayName} size={28} tip={false} />
                  <span className={styles.rowText}>
                    <span className={styles.ellipsis} style={{ fontSize: 13 }}>
                      {r.displayName}
                      {already && (
                        <Badge size="small" appearance="tint" color="subtle" style={{ marginLeft: 6 }}>
                          배정됨
                        </Badge>
                      )}
                    </span>
                    <Caption1 className={styles.ellipsis} style={{ color: tokens.colorNeutralForeground3 }}>
                      {r.roleTitle}
                    </Caption1>
                  </span>
                  <span className={styles.util} style={{ color: wl?.isOverAllocated ? tokens.colorPaletteRedForeground1 : pct === 0 ? tokens.colorNeutralForeground4 : tokens.colorNeutralForeground2 }}>
                    {pct}%
                  </span>
                </button>
              );
            })}
            {list.length === 0 && <Caption1 style={{ padding: tokens.spacingHorizontalM, color: tokens.colorNeutralForeground3 }}>조건에 맞는 인력이 없습니다.</Caption1>}
          </div>
        </div>
      </DrawerBody>
    </OverlayDrawer>
    {editing && (
      <ResourceFormDialog
        open
        initial={editing}
        teams={Object.values(teams)}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          updateResource(editing.id, data);
          setEditing(null);
        }}
      />
    )}
    </>
  );
}
